/* eslint-disable @typescript-eslint/no-explicit-any */
// newTemplateMessageSendSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addEsChatToConfig } from "../../Utils/apiUtils";
import axios from 'axios';

interface TemplateMessageVariables {
  [key: string]: string;
}

interface SendNewTemplateMessagePayload {
  template: string;
  variables: TemplateMessageVariables;
  current_recipient: string;
  scheduled_time?: number;
}

interface NewTemplateMessageState {
  variables: TemplateMessageVariables;
  isSending: boolean;
  error: string | null;
}

const initialState: NewTemplateMessageState = {
  variables: {},
  isSending: false,
  error: null
};

export const sendNewTemplateMessage = createAsyncThunk<
  any,
  SendNewTemplateMessagePayload,
  { rejectValue: string }
>(
  'newTemplateMessage/send',
  async (payload, { rejectWithValue }) => {
    try {
      const config = addEsChatToConfig();
      
      const response = await axios.post('https://api.insightssystem.com/api:bVXsw_FD/web/send/template', payload, config);
      const result = response.data;
      
      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from API response
        let errorMessage = "Message could not be sent. Please try again.";

        // First try to get error from the direct response structure
        if (result.message) {
          errorMessage = result.message;
        } else {
          // Try to extract error message from WhatsApp response based on available structure
          const whatsappResponse = result.whatsapp_response_info as any;
          
          if (whatsappResponse) {
            if (whatsappResponse.error_user_msg) {
              // First structure - has error_user_msg
              errorMessage = whatsappResponse.error_user_msg;
            } else if (whatsappResponse.error_data?.details) {
              // Second structure - has error_data.details
              errorMessage = whatsappResponse.error_data.details;
            } else if (whatsappResponse.message) {
              // Fallback to general message if available
              errorMessage = whatsappResponse.message;
            }
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      return result;
    } catch (error: any) {
      // Handle axios errors with response data
      if (error.response) {
        const responseData = error.response.data;
        
        // Try to extract error message from the response
        if (responseData && responseData.message) {
          return rejectWithValue(responseData.message);
        }
        
        // Fallback to status text or generic message
        return rejectWithValue(error.response.statusText || `Request failed with status code ${error.response.status}`);
      }
      
      // Handle other errors
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  }
);

const newTemplateMessageSendSlice = createSlice({
  name: 'newTemplateMessage',
  initialState,
  reducers: {
    updateNewTemplateVariable: (state, action) => {
      const { key, value } = action.payload;
      state.variables[key] = value;
    },
    resetNewTemplateVariables: (state) => {
      state.variables = {};
    },
    clearNewTemplateError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendNewTemplateMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendNewTemplateMessage.fulfilled, (state) => {
        state.isSending = false;
        state.error = null;
        state.variables = {};
      })
      .addCase(sendNewTemplateMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload ?? 'Failed to send template message';
      });
  }
});

export const {
  updateNewTemplateVariable,
  resetNewTemplateVariables,
  clearNewTemplateError
} = newTemplateMessageSendSlice.actions;

type NewTemplateMessageRootPick = { newTemplateMessage: NewTemplateMessageState };
export const selectNewTemplateVariables = (state: NewTemplateMessageRootPick) =>
  state.newTemplateMessage.variables;
export const selectNewTemplateIsSending = (state: NewTemplateMessageRootPick) =>
  state.newTemplateMessage.isSending;
export const selectNewTemplateError = (state: NewTemplateMessageRootPick) =>
  state.newTemplateMessage.error;

export default newTemplateMessageSendSlice.reducer;